import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Award, Leaf, Globe } from 'lucide-react';
import { HERO_SLIDES, CATEGORIES, SITE, getNewProducts } from '../mock/mock';
import ProductCard from '../components/ProductCard';

const Home = () => {
  const [slide, setSlide] = useState(0);
  const newProducts = getNewProducts().slice(0, 4);

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 6000);
    return () => clearInterval(t);
  }, []);

  const current = HERO_SLIDES[slide];

  return (
    <div>
      {/* HERO */}
      <section className="relative h-[75vh] min-h-[480px] overflow-hidden">
        {HERO_SLIDES.map((s, i) => (
          <div
            key={s.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${i === slide ? 'opacity-100' : 'opacity-0'}`}
          >
            <img src={s.image} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/25" />
          </div>
        ))}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
          <h1 className="hero-outline text-5xl sm:text-7xl md:text-8xl leading-[0.95]">{current.title}</h1>
          <h1 className="hero-outline text-4xl sm:text-6xl md:text-7xl leading-[0.95] mt-2">{current.title2}</h1>
          <Link to={current.link} className="mt-8 ps-yellow-bg text-black px-8 py-4 rounded-full font-oswald font-600 uppercase tracking-wide text-base sm:text-lg btn-press hover:bg-white flex items-center gap-2 shadow-xl">
            {current.cta} <ArrowRight size={20} />
          </Link>
        </div>
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
          {HERO_SLIDES.map((_, i) => (
            <button key={i} onClick={() => setSlide(i)} className={`h-2.5 rounded-full transition-all ${i === slide ? 'w-8 ps-yellow-bg' : 'w-2.5 bg-white/60'}`} />
          ))}
        </div>
      </section>

      {/* trust bar */}
      <section className="ps-yellow-bg">
        <div className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: ShieldCheck, t: 'Certified Non-Toxic' },
            { icon: Award, t: 'Premium Quality' },
            { icon: Leaf, t: 'Eco-Friendly' },
            { icon: Globe, t: 'Sold in 75 Countries' },
          ].map((f, i) => (
            <div key={i} className="flex items-center gap-3 justify-center md:justify-start">
              <f.icon size={30} className="text-[#1a1ad6]" />
              <span className="font-oswald uppercase text-sm sm:text-base text-black font-600">{f.t}</span>
            </div>
          ))}
        </div>
      </section>

      {/* CATEGORY GRID */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <h2 className="font-anton text-4xl sm:text-5xl text-center ps-blue-text mb-3">{SITE.tagline}</h2>
        <p className="text-center text-gray-500 max-w-2xl mx-auto mb-12">Explore our full range of premium toys and accessories, crafted with love for your pets and home.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
          {CATEGORIES.map((c) => (
            <Link key={c.slug} to={`/category/${c.slug}`} className="cat-card group relative rounded-2xl overflow-hidden shadow-md aspect-square">
              <img src={c.image} alt={c.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              {c.isNew && <span className="absolute top-3 right-3 ps-yellow-bg text-black text-xs font-bold px-2.5 py-1 rounded-full font-oswald uppercase shadow">New</span>}
              <div className="cat-label absolute bottom-0 left-0 right-0 bg-white/95 py-3 px-4 text-center">
                <span className="font-oswald uppercase tracking-wide text-[#1a1ad6] text-sm sm:text-base font-600">{c.name}</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* NEW PRODUCTS */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="font-oswald uppercase tracking-widest text-sm text-[#ffd400] bg-[#1a1ad6] px-3 py-1 rounded">Just Arrived</span>
              <h2 className="font-anton text-4xl ps-blue-text mt-3">NEWEST PRODUCTS</h2>
            </div>
            <Link to="/new" className="hidden sm:flex items-center gap-1 font-oswald uppercase text-[#1a1ad6] hover:gap-2 transition-all">View all <ArrowRight size={18} /></Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {newProducts.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-12 items-center">
        <div className="relative">
          <img src="https://images.unsplash.com/photo-1534361960057-19889db9621e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGRvZyUyMHBsYXlpbmd8ZW58MHx8fHwxNzg3ODY2MTI2fDA&ixlib=rb-4.1.0&q=85" alt="About us" className="rounded-3xl shadow-xl w-full h-[420px] object-cover" />
          <div className="absolute -bottom-6 -right-4 ps-yellow-bg rounded-2xl px-6 py-4 shadow-xl">
            <span className="font-anton text-4xl text-[#1a1ad6]">200+</span>
            <p className="font-oswald uppercase text-xs text-black">Products Worldwide</p>
          </div>
        </div>
        <div>
          <h2 className="font-anton text-4xl ps-blue-text mb-5">ABOUT US</h2>
          <p className="text-gray-600 leading-relaxed mb-4">In 1995, our story started with an idea of quality toys for our baby, a labrador puppy named Daisy. We designed the first tennis ball made specifically for dogs.</p>
          <p className="text-gray-600 leading-relaxed mb-4">Since our founding, we have grown to manufacture over 200 products distributed in 75 countries worldwide. We design safe, durable, premium quality and environmentally friendly toys and accessories at affordable prices.</p>
          <p className="text-gray-600 leading-relaxed mb-6">All our toys are certified non-toxic by independent third-party laboratories, and play-tested to make sure they are extra durable and safe in real world environments.</p>
          <Link to="/products" className="inline-flex items-center gap-2 ps-blue-bg text-white px-7 py-3.5 rounded-full font-oswald uppercase tracking-wide btn-press hover:bg-[#0f0fa0]">Explore Products <ArrowRight size={18} /></Link>
        </div>
      </section>

      {/* WHOLESALE CTA */}
      <section className="ps-blue-bg">
        <div className="max-w-7xl mx-auto px-6 py-16 text-center">
          <h2 className="font-anton text-3xl sm:text-4xl text-white mb-4">Find Out More About Wholesale Pricing</h2>
          <p className="text-white/70 max-w-xl mx-auto mb-8">Interested in stocking our products? Get in touch with our team for wholesale rates and custom logo options.</p>
          <Link to="/contact" className="inline-flex items-center gap-2 ps-yellow-bg text-black px-8 py-4 rounded-full font-oswald uppercase tracking-wide text-lg btn-press hover:bg-white">Contact Us <ArrowRight size={20} /></Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
